import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { 
  BookOpen, 
  Wifi, 
  FileText, 
  Plus,
  Eye,
  Edit,
  Trash2,
  Search
} from 'lucide-react'
import { useFirebase } from '../contexts/FirebaseContext'

const OnlineTests = () => {
  const [homeContents, setHomeContents] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const navigate = useNavigate()
  const { getHomeContents, getTestQuestions, deleteHomeContent } = useFirebase()

  // Utility function to safely format dates
  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A'
    
    try {
      let date
      if (dateValue instanceof Date) {
        date = dateValue
      } else if (dateValue.toDate && typeof dateValue.toDate === 'function') {
        date = dateValue.toDate()
      } else {
        date = new Date(dateValue)
      }
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date'
      }
      
      return date.toLocaleDateString()
    } catch (error) {
      console.error('Error formatting date:', error)
      return 'Invalid Date'
    }
  }

  useEffect(() => {
    loadHomeContents()
  }, [])

  const loadHomeContents = async () => {
    try {
      setLoading(true)
      const contents = await getHomeContents()
      
      // Get test counts for each content
      const contentsWithTestCounts = await Promise.all(
        contents.map(async (content) => {
          try {
            const tests = await getTestQuestions(content.id)
            if (tests && tests.length > 0) {
              // Count unique tests by grouping by test name/title
              const uniqueTests = tests.reduce((acc, test) => {
                const testName = test.testName || test.title || test.testTitle || 'Untitled Test'
                acc[testName] = true
                return acc
              }, {})
              return {
                ...content,
                testCount: Object.keys(uniqueTests).length
              }
            }
            return {
              ...content,
              testCount: 0
            }
          } catch (error) {
            return {
              ...content,
              testCount: 0
            }
          }
        })
      )
      
      setHomeContents(contentsWithTestCounts)
    } catch (error) {
      console.error('Error loading home contents:', error)
      setHomeContents([])
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteContent = async (contentId, contentTitle) => {
    if (window.confirm(`Are you sure you want to delete "${contentTitle}"? This action cannot be undone.`)) {
      try {
        setLoading(true);
        await deleteHomeContent(contentId);
        // Reload the content list
        await loadHomeContents();
        alert('Content deleted successfully!');
      } catch (error) {
        console.error('Error deleting content:', error);
        alert('Error deleting content. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  }

  const filteredContents = homeContents.filter(content =>
    content.title.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-2xl shadow-xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2 flex items-center">
              <Wifi className="h-8 w-8 mr-3" />
              Online Tests
            </h1>
            <p className="text-green-100">
              Manage online test content and create real-time assessments
            </p>
          </div>
          <Link
            to="/home-content/add"
            className="bg-white text-green-600 px-6 py-3 rounded-lg font-medium hover:bg-green-50 transition-colors flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Content
          </Link>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search content by title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Content List */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Online Test Contents ({filteredContents.length})</h2>
          <p className="text-gray-600">Select content to manage online tests</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        ) : filteredContents.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-600 uppercase text-sm">
                    Content Details
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 uppercase text-sm">
                    Tests Count
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 uppercase text-sm">
                    Manage Tests
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 uppercase text-sm">
                    Status
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 uppercase text-sm">
                    Date
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 uppercase text-sm">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredContents.map((content) => (
                  <tr key={content.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                          {content.imageUrl ? (
                            <img 
                              src={content.imageUrl} 
                              alt={content.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : (
                            <BookOpen className="w-6 h-6 text-gray-400" />
                          )}
                          <div className="w-full h-full bg-gray-100 flex items-center justify-center" style={{display: 'none'}}>
                            <BookOpen className="w-6 h-6 text-gray-400" />
                          </div>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{content.title}</div>
                          <div className="text-sm text-gray-500">
                            Created: {formatDate(content.createdAt)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-left">
                      <div className="flex items-center justify-center space-x-1">
                        <FileText className="w-4 h-4 text-green-500" />
                        <span className="font-medium text-green-600">{content.testCount || 0}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-left">
                      <button
                        onClick={() => navigate(`/tests-management/${content.id}`)}
                        className="p-2 bg-green-100 text-green-600 rounded-full hover:bg-green-200 transition-colors"
                        title="Manage Online Tests"
                      >
                        <Wifi className="w-4 h-4" />
                      </button>
                    </td>
                    <td className="py-4 px-4 text-left">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        content.status === 'published' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {content.status || 'published'}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-left text-sm text-gray-600">
                      {formatDate(content.createdAt)}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-center space-x-2">
                        <button
                          onClick={() => navigate(`/home-content/view/${content.id}`)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                          title="View Content"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/home-content/edit/${content.id}`)}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                          title="Edit Content"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteContent(content.id, content.title)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                          title="Delete Content"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Wifi className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No content available</h3>
            <p className="text-gray-600 mb-4">Create your first content to get started with online tests.</p>
            <Link
              to="/home-content/add"
              className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add First Content
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}

export default OnlineTests
